import axios from "axios";
import { PurgeImpactedOrg, PurgeQueuedResult } from "./purgeTypes";

const apiUrl = process.env.REACT_APP_API_URL;
const clientApiUrl = process.env.REACT_APP_CLIENT_API_URL;

const OrganizationAPI = {
	getOrganizations: () => {
		return axios
			.get(`${apiUrl}/organizations`)
			.then((response) => response.data as OrganizationDTO[]);
	},
	getOrganizationNames: (organization: OrganizationsNamesRequest) => {
		return axios
			.get(`${apiUrl}/organization-names/${organization.id}`)
			.then((response) => response.data as string[]);
	},
	getOrganization: (organization: OrganizationRequest) => {
		return axios
			.get(`${apiUrl}/organizations/${organization.id}`)
			.then((response) => response.data as OrganizationDTO);
	},
	updateOrganization: (organizationItem: OrganizationUpsertRequest) => {
		return axios
			.post(`${apiUrl}/organizations`, organizationItem)
			.then((response) => response.data as OrganizationDTO);
	},
	getOrganizationRole: (request: OrganizationRoleRequest) => {
		return axios
			.get(`${apiUrl}/organizations/${request.id}/roles/${request.roleId}`)
			.then((response) => response.data as RoleDTO);
	},
	getOrganizationRoles: (request: OrganizationRolesRequest) => {
		return axios
			.get(`${apiUrl}/organizations/${request.id}/roles`)
			.then((response) => response.data as RoleDTO[]);
	},
	updateOrganizationRole: (organizationRole: OrganizationRoleUpsertRequest) => {
		return axios
			.post(
				`${apiUrl}/organizations/${organizationRole.id}/roles`,
				organizationRole
			)
			.then((response) => response.data as RoleDTO);
	},
	getPermissions: () => {
		return axios
			.get(`${apiUrl}/permissions`)
			.then((response) => response.data as PermissionDTO[]);
	},
	getDatasetEvaluators: (request: GetDatasetEvaluatorsRequest) => {
		return axios
			.get(
				`${apiUrl}/organizations/${request.tenantId}/datasets/${request.datasetId}/evaluators`
			)
			.then((response) => response.data as OrganizationUserDTO[]);
	},
	getEvaluator: (request: GetEvaluatorRequest) => {
		return axios
			.get(
				`${apiUrl}/organizations/${request.tenantId}/datasets/${request.datasetId}/evaluator/${request.evaluatorId}`
			)
			.then((response) => response.data as OrganizationUserDTO);
	},
	getOrganizationUsers: (request: OrganizationUsersRequest) => {
		return axios
			.get(`${apiUrl}/organizations/${request.id}/users`)
			.then((response) => response.data as UserDetailsDTO[]);
	},
	getOrganizationActiveUsers: (request: OrganizationActiveUsersRequest) => {
		return axios
			.get(`${apiUrl}/organizations/${request.organizationId}/active-users`)
			.then((response) => response.data as UserDetailsDTO[]);
	},
	getOrganizationUser: (request: GetOrganizationUserRequest) => {
		return axios
			.get(
				`${apiUrl}/organizations/${request.tenantId}/users/${request.userId}`
			)
			.then((response) => response.data as OrganizationUserDTO);
	},
	getOrganizationUserEmails: (request: OrganizationUserEmailsRequest) => {
		return axios
			.get(`${apiUrl}/organizations/${request.id}/user-emails`)
			.then((response) => response.data as string[]);
	},
	getOrganizationEmailExists: (request: UserEmailExistRequest) => {
		return axios
			.get(`${apiUrl}/organizations/exists-emails?email=${request.email}`)
			.then((response) => response.data as boolean);
	},
	getOrganizationProducts: (request: GetProductsRequest) => {
		return axios
			.get(`${apiUrl}/organizations/${request.organizationId}/products`)
			.then((response) => response.data as ProductDTO[]);
	},
	postOrganizationLogo: (logo: File) => {
		const data = new FormData();
		data.append("file", logo);

		return axios
			.post(`${apiUrl}/organizations/logo-upload`, data)
			.then((response) => response.data as string);
	},
	postOrganizationUser: (request: UpsertOrganizationUserRequest) => {
		return axios
			.post(`${apiUrl}/organizations/users`, request)
			.then((response) => response.data as ImpiricaUser)
			.catch((err) => {
				throw err.response.data.responseStatus.message;
			});
	},
	putOrganizationUserDetails: (request: PutOrganizationUserDetailsRequest) => {
		return axios.put(
			`${apiUrl}/organizations/${request.organizationId}/users/${request.userId}/details`,
			request
		);
	},

	// client site methods
	getClientSiteOrganizationUser: (
		request: GetClientSiteOrganizationUserRequest
	) => {
		return axios
			.get(
				`${clientApiUrl}/organizations/${request.tenantId}/users/${request.userId}`
			)
			.then((response) => response.data as OrganizationUserDTO);
	},

	getClientSiteOrganization: (req: GetClientSiteOrganizationRequest) => {
		return axios
			.get(`${clientApiUrl}/organizations/${req.organizationId}`)
			.then((res) =>
				res ? (res.data as OrganizationDTO) : ({} as OrganizationDTO)
			);
	},

	getCommercialDrivingExpSettings: (
		req: GetCommercialDrivingExpSettingsRequest
	) => {
		return axios
			.get(
				`${apiUrl}/organizations/${req.tenantId}/datasets/${req.datasetId}/commdrvexpsettings`
			)
			.then((res) =>
				res
					? (res.data as CommercialDrivingExpSettingsDTO)
					: ({} as CommercialDrivingExpSettingsDTO)
			);
	},

	getPurgeImpactedOrgs: (organizationId: number) => {
		return axios
			.get(`${apiUrl}/organizations/${organizationId}/purge-impacted-orgs`)
			.then((response) => response.data as PurgeImpactedOrg[]);
	},

	enqueuePurge: (organizationId: number) => {
		return axios
			.post(`${apiUrl}/organizations/${organizationId}/purge`)
			.then((response) => response.data as PurgeQueuedResult);
	},
};

export default OrganizationAPI;
